import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import {
  Warning, Plus, Funnel, CaretDown, XCircle, CheckCircle, Lightning, Flask,
  Thermometer, TagSimple
} from '@phosphor-icons/react';

const CATEGORIES = ['contamination', 'reagent_failure', 'equipment_malfunction', 'protocol_error', 'human_error', 'environmental', 'unknown'];
const SEVERITIES = ['low', 'medium', 'high', 'critical'];
const SEVERITY_COLORS = { low: '#22c55e', medium: '#eab308', high: '#f97316', critical: '#ef4444' };

const FailureDashboardPage = () => {
  const [logs, setLogs] = useState([]);
  const [patterns, setPatterns] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [filterCat, setFilterCat] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [form, setForm] = useState({ title: '', category: 'unknown', severity: 'medium', description: '', root_cause: '', corrective_action: '', reagents_involved: '', equipment_involved: '', tags: '' });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [l, p] = await Promise.all([
        api.getFailureLogs(filterCat || undefined, filterStatus || undefined),
        api.getFailurePatterns()
      ]);
      setLogs(l);
      setPatterns(p);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [filterCat, filterStatus]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.createFailureLog({
        ...form,
        reagents_involved: form.reagents_involved ? form.reagents_involved.split(',').map(s => s.trim()) : [],
        equipment_involved: form.equipment_involved ? form.equipment_involved.split(',').map(s => s.trim()) : [],
        tags: form.tags ? form.tags.split(',').map(s => s.trim()) : [],
      });
      toast.success('Failure logged');
      setShowForm(false);
      setForm({ title: '', category: 'unknown', severity: 'medium', description: '', root_cause: '', corrective_action: '', reagents_involved: '', equipment_involved: '', tags: '' });
      load();
    } catch (e) { toast.error('Failed to log'); }
  };

  const resolve = async (id) => {
    await api.updateFailureLog(id, { status: 'resolved' });
    toast.success('Marked as resolved');
    load();
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div data-testid="failure-dashboard">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="heading-display">Failure Dashboard</h1>
          <p className="text-slate-400 mt-1">Track, analyze, and learn from experimental failures</p>
        </div>
        <Button className="btn-primary flex items-center gap-2" onClick={() => setShowForm(true)} data-testid="log-failure-btn">
          <Plus size={16} /> Log Failure
        </Button>
      </div>

      {/* Stats Cards */}
      {patterns && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="failure-stats">
          <div className="card-flat p-4 text-center">
            <p className="text-3xl font-bold" style={{ color: '#F1F5F9' }}>{patterns.total}</p>
            <p className="text-xs text-slate-400 mt-1">Total Failures</p>
          </div>
          <div className="card-flat p-4 text-center">
            <p className="text-3xl font-bold text-red-500">{patterns.open}</p>
            <p className="text-xs text-slate-400 mt-1">Open Issues</p>
          </div>
          <div className="card-flat p-4 text-center">
            <p className="text-3xl font-bold text-green-500">{patterns.total - patterns.open}</p>
            <p className="text-xs text-slate-400 mt-1">Resolved</p>
          </div>
          <div className="card-flat p-4 text-center">
            <p className="text-3xl font-bold text-amber-500">{patterns.by_category?.length || 0}</p>
            <p className="text-xs text-slate-400 mt-1">Categories</p>
          </div>
        </div>
      )}

      {/* Pattern Analysis */}
      {patterns && (patterns.top_reagent_failures?.length > 0 || patterns.top_equipment_failures?.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {patterns.top_reagent_failures?.length > 0 && (
            <div className="card-flat p-4">
              <h3 className="heading-card mb-3 flex items-center gap-2"><Flask size={14} className="text-red-500" /> Top Reagent Failures</h3>
              <div className="space-y-2">
                {patterns.top_reagent_failures.map((r, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span className="text-slate-200">{r.reagent}</span>
                    <span className="text-xs px-2 py-0.5 bg-red-100 text-red-400 rounded-full">{r.count} failures</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {patterns.top_equipment_failures?.length > 0 && (
            <div className="card-flat p-4">
              <h3 className="heading-card mb-3 flex items-center gap-2"><Thermometer size={14} className="text-orange-500" /> Top Equipment Failures</h3>
              <div className="space-y-2">
                {patterns.top_equipment_failures.map((e, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span className="text-slate-200">{e.equipment}</span>
                    <span className="text-xs px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full">{e.count} failures</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4">
        <Funnel size={16} className="text-slate-400" />
        <select value={filterCat} onChange={e => setFilterCat(e.target.value)} className="input-field text-sm py-1.5 w-40" data-testid="filter-category">
          <option value="">All Categories</option>
          {CATEGORIES.map(c => <option key={c} value={c}>{c.replace('_', ' ')}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="input-field text-sm py-1.5 w-32" data-testid="filter-status">
          <option value="">All Status</option>
          <option value="open">Open</option>
          <option value="resolved">Resolved</option>
        </select>
      </div>

      {/* Failure Logs */}
      <div className="space-y-3">
        {logs.length === 0 ? (
          <div className="card-flat p-8 text-center text-slate-400">
            <Warning size={40} className="mx-auto mb-2 opacity-40" />
            <p>No failures logged yet. That's great... or maybe nobody is tracking them.</p>
          </div>
        ) : logs.map(log => (
          <div key={log.id} className="card-flat p-4 hover:border-slate-700 transition-colors" data-testid={`failure-${log.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: SEVERITY_COLORS[log.severity] }} />
                  <h3 className="font-semibold text-sm" style={{ color: '#F1F5F9' }}>{log.title}</h3>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-300 capitalize">{log.category?.replace('_', ' ')}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${log.status === 'open' ? 'bg-red-100 text-red-400' : 'bg-green-100 text-green-400'}`}>{log.status}</span>
                </div>
                {log.description && <p className="text-xs text-slate-400 mt-1 line-clamp-2">{log.description}</p>}
                <div className="flex items-center gap-3 mt-2 text-[10px] text-slate-400">
                  <span>{log.created_by_name}</span>
                  <span>{new Date(log.created_at).toLocaleDateString()}</span>
                  {log.reagents_involved?.length > 0 && <span className="flex items-center gap-1"><Flask size={10} /> {log.reagents_involved.join(', ')}</span>}
                </div>
              </div>
              {log.status === 'open' && (
                <Button size="sm" variant="outline" className="text-xs" onClick={() => resolve(log.id)} data-testid={`resolve-${log.id}`}>
                  <CheckCircle size={14} className="mr-1" /> Resolve
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Log Failure Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-slate-800 rounded-xl p-6 w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="failure-form">
            <div className="flex items-center justify-between mb-4">
              <h2 className="heading-section">Log Failure</h2>
              <button onClick={() => setShowForm(false)}><XCircle size={20} /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-3">
              <input className="input-field w-full" placeholder="Failure title *" required value={form.title} onChange={e => setForm(p => ({...p, title: e.target.value}))} data-testid="failure-title-input" />
              <div className="grid grid-cols-2 gap-3">
                <select className="input-field" value={form.category} onChange={e => setForm(p => ({...p, category: e.target.value}))} data-testid="failure-category-select">
                  {CATEGORIES.map(c => <option key={c} value={c}>{c.replace('_', ' ')}</option>)}
                </select>
                <select className="input-field" value={form.severity} onChange={e => setForm(p => ({...p, severity: e.target.value}))} data-testid="failure-severity-select">
                  {SEVERITIES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <textarea className="input-field w-full" rows={3} placeholder="Description" value={form.description} onChange={e => setForm(p => ({...p, description: e.target.value}))} />
              <textarea className="input-field w-full" rows={2} placeholder="Root cause (if known)" value={form.root_cause} onChange={e => setForm(p => ({...p, root_cause: e.target.value}))} />
              <textarea className="input-field w-full" rows={2} placeholder="Corrective action" value={form.corrective_action} onChange={e => setForm(p => ({...p, corrective_action: e.target.value}))} />
              <input className="input-field w-full" placeholder="Reagents involved (comma-separated)" value={form.reagents_involved} onChange={e => setForm(p => ({...p, reagents_involved: e.target.value}))} />
              <input className="input-field w-full" placeholder="Equipment involved (comma-separated)" value={form.equipment_involved} onChange={e => setForm(p => ({...p, equipment_involved: e.target.value}))} />
              <Button type="submit" className="btn-primary w-full" data-testid="submit-failure-btn">Log Failure</Button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FailureDashboardPage;
